import { BlockBaseCommand } from './BlockBaseCommand';
import type { BlockCommandDependencies } from './BlockBaseCommand';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { addGridBlockState, deleteGridBlockState } from '../grid/reduxStore/editorSlice';
import { GridBlockType } from 'services/entities/BlockTypes';
import { addSignatureViaSocket, deleteSignatureViaSocket } from '../grid/reduxStore/saveHandlers';

export interface SignatureDeleteCommandParams {
  sectionId: string;
  signature: SignatureBox;
  blockConfig: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    z: number;
  };
}

export class SignatureDeleteCommand extends BlockBaseCommand {
  readonly sectionId: string;
  private readonly signatureId: string;
  private readonly signature: SignatureBox;
  private readonly blockConfig: SignatureDeleteCommandParams['blockConfig'];

  constructor(dependencies: BlockCommandDependencies, params: SignatureDeleteCommandParams) {
    super(dependencies);
    this.sectionId = params.sectionId;
    this.signature = structuredClone(params.signature);
    this.signatureId = params.signature.signatureBoxId;
    this.blockConfig = { ...params.blockConfig };
  }

  getCurrentBlockId(): string {
    return this.signatureId;
  }

  applyOptimistic(): void {
    this.setSaving();

    this.dispatch(
      deleteGridBlockState({
        sectionId: this.sectionId,
        blockId: this.signatureId,
      })
    );

    this.recalculatePageHeight(this.sectionId);
  }

  async persistToSocket(): Promise<boolean> {
    try {
      const response = await deleteSignatureViaSocket(this.socketClient, this.signature);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackDelete();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackDelete();
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          ...(response.errorCode ? { errorCode: response.errorCode } : null),
        })
      );
      return true;
    } catch (error) {
      console.error('SignatureDeleteCommand: persistToSocket failed', error);
      this.rollbackDelete();
      return false;
    }
  }

  async undo(): Promise<boolean> {
    try {
      this.setSaving();

      this.dispatch(
        addGridBlockState({
          sectionId: this.sectionId,
          blockId: this.signatureId,
          content: this.signature,
          blockConfig: this.blockConfig,
          blockType: GridBlockType.SIGNATURE,
        })
      );

      this.recalculatePageHeight(this.sectionId);

      const response = await addSignatureViaSocket(this.socketClient, this.sectionId, this.signature);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackUndo();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackUndo();
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          ...(response.errorCode ? { errorCode: response.errorCode } : null),
        })
      );
      return true;
    } catch (error) {
      console.error('SignatureDeleteCommand: undo failed', error);
      this.rollbackUndo();
      return false;
    }
  }

  /**
   * Redo — re-delete from Redux + SIGNATURE_DELETE socket.
   */
  async redo(): Promise<boolean> {
    try {
      this.setSaving();

      this.dispatch(
        deleteGridBlockState({
          sectionId: this.sectionId,
          blockId: this.signatureId,
        })
      );

      this.recalculatePageHeight(this.sectionId);

      const response = await deleteSignatureViaSocket(this.socketClient, this.signature);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackDelete();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackDelete();
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          ...(response.errorCode ? { errorCode: response.errorCode } : null),
        })
      );
      return true;
    } catch (error) {
      console.error('SignatureDeleteCommand: redo failed', error);
      this.rollbackDelete();
      return false;
    }
  }

  /**
   * Rollback failed delete — re-add the signature block.
   */
  private rollbackDelete(): void {
    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: this.signatureId,
        content: this.signature,
        blockConfig: this.blockConfig,
        blockType: GridBlockType.SIGNATURE,
      })
    );
    this.setNotSaved();
  }

  /**
   * Rollback failed undo — re-delete the signature block.
   */
  private rollbackUndo(): void {
    this.dispatch(
      deleteGridBlockState({
        sectionId: this.sectionId,
        blockId: this.signatureId,
      })
    );
    this.setNotSaved();
  }

  toJSON(): Record<string, any> {
    return {
      signatureId: this.signatureId,
      sectionId: this.sectionId,
      signature: this.signature,
    };
  }
}
